import type { SqlImportPreviewInput } from '@/services/db/sql-import/mutations';
import type { SqlImportPreviewResult, SqlImportsImportRelationship } from '@customerio/hydra-ui-api';
import { ImportType } from '@/services/db/sql-import/entity';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import { isEmptyString } from '@/utils/string';

// cspell:ignore Montréal northamerica southamerica -- Google's region ids and city names
type LocationGroup = { label: string; options: { label: string; value: string }[] };

/** The datasets a BigQuery query can run in, grouped as Google lists them. */
export const BIG_QUERY_LOCATIONS = [
  {
    label: 'Multi-regional',
    options: [
      { label: 'US', value: 'US' },
      { label: 'EU', value: 'EU' },
    ],
  },
  {
    label: 'Americas',
    options: [
      { label: 'Iowa (us-central1)', value: 'us-central1' },
      { label: 'Las Vegas (us-west4)', value: 'us-west4' },
      { label: 'Los Angeles (us-west2)', value: 'us-west2' },
      { label: 'Montréal (northamerica-northeast1)', value: 'northamerica-northeast1' },
      { label: 'Northern Virginia (us-east4)', value: 'us-east4' },
      { label: 'Oregon (us-west1)', value: 'us-west1' },
      { label: 'Salt Lake City (us-west3)', value: 'us-west3' },
      { label: 'São Paulo (southamerica-east1)', value: 'southamerica-east1' },
      { label: 'Santiago (southamerica-west1)', value: 'southamerica-west1' },
      { label: 'South Carolina (us-east1)', value: 'us-east1' },
      { label: 'Toronto (northamerica-northeast2)', value: 'northamerica-northeast2' },
    ],
  },
  {
    label: 'Europe',
    options: [
      { label: 'Belgium (europe-west1)', value: 'europe-west1' },
      { label: 'Finland (europe-north1)', value: 'europe-north1' },
      { label: 'Frankfurt (europe-west3)', value: 'europe-west3' },
      { label: 'London (europe-west2)', value: 'europe-west2' },
      { label: 'Netherlands (europe-west4)', value: 'europe-west4' },
      { label: 'Warsaw (europe-central2)', value: 'europe-central2' },
      { label: 'Zürich (europe-west6)', value: 'europe-west6' },
    ],
  },
  {
    label: 'Asia Pacific',
    options: [
      { label: 'Delhi (asia-south2)', value: 'asia-south2' },
      { label: 'Hong Kong (asia-east2)', value: 'asia-east2' },
      { label: 'Jakarta (asia-southeast2)', value: 'asia-southeast2' },
      { label: 'Melbourne (australia-southeast2)', value: 'australia-southeast2' },
      { label: 'Mumbai (asia-south1)', value: 'asia-south1' },
      { label: 'Osaka (asia-northeast2)', value: 'asia-northeast2' },
      { label: 'Seoul (asia-northeast3)', value: 'asia-northeast3' },
      { label: 'Singapore (asia-southeast1)', value: 'asia-southeast1' },
      { label: 'Sydney (australia-southeast1)', value: 'australia-southeast1' },
      { label: 'Taiwan (asia-east1)', value: 'asia-east1' },
      { label: 'Tokyo (asia-northeast1)', value: 'asia-northeast1' },
    ],
  },
] satisfies LocationGroup[];

/** The most rows a preview shows; the backend returns no more than this. */
export const MAX_PREVIEW_ROWS = 100;

/** The relationship half of an import as the wizard edits it: the wire's fields, every one present. */
export type SqlImportRelationship = {
  [K in keyof SqlImportsImportRelationship]-?: NonNullable<SqlImportsImportRelationship[K]>;
};

type DefaultQueryInput = {
  databaseType: SqlDatabaseType;
  /** The identifier the import matches on; `id` when nothing is chosen yet. */
  idFieldName: string;
  isObjectImport: boolean;
  relationship?: Pick<SqlImportRelationship, 'entity1_id_field_name' | 'entity2_id_field_name'>;
};

/**
 * The example query a new import starts with, in the shape Ember offered: the identifier and the
 * usual attribute columns, limited to rows changed since the last sync.
 */
export function defaultSqlImportQuery({
  databaseType,
  idFieldName,
  isObjectImport,
  relationship,
}: DefaultQueryInput): string {
  const id = isEmptyString(idFieldName) ? 'id' : idFieldName;
  if (relationship != null) {
    return `SELECT person_id AS ${relationship.entity1_id_field_name}, company_id AS ${relationship.entity2_id_field_name}\nFROM my_table\nWHERE last_updated > {{last_sync_time}}\n`;
  }
  if (isObjectImport) {
    return `SELECT id AS ${id}\nFROM my_table\nWHERE last_updated > {{last_sync_time}}\n`;
  }
  if (databaseType === 'snowflake') {
    return `SELECT ${id} AS "${id}", EMAIL AS "email", PHONE_NUMBER AS "phone_number"\nFROM my_table\nWHERE last_updated > {{last_sync_time}}\n`;
  }
  return `SELECT ${id}, email, first_name, primary_phone AS phone\nFROM my_table\nWHERE last_updated > {{last_sync_time}}\n`;
}

type PreviewBodyInput = {
  query: string;
  databaseId: string;
  /** The saved import the preview belongs to, once it has one. */
  importId: string | undefined;
  idFieldName: string;
  location: string | undefined;
  importType: ImportType;
  objectTypeId: string | undefined;
  /** Set for the relationships query; the body then describes both entities. */
  relationship?: SqlImportRelationship;
};

/**
 * The preview request, field for field what Ember sent: the query, how rows are identified, the
 * database, the import it belongs to, and for BigQuery the dataset location. A relationships
 * preview carries both entities instead and tells the backend so through `import_type`.
 */
export function buildPreviewBody({
  query,
  databaseId,
  importId,
  idFieldName,
  location,
  importType,
  objectTypeId,
  relationship,
}: PreviewBodyInput): SqlImportPreviewInput {
  const base: SqlImportPreviewInput = {
    query,
    id_field_name: isEmptyString(idFieldName) ? 'id' : idFieldName,
    database_id: databaseId,
    import_id: importId,
    location: location ?? '',
  };
  const body: SqlImportPreviewInput =
    relationship == null
      ? { ...base, import_type: importType }
      : {
          ...base,
          import_type: 'relationship',
          entity1_type_id: relationship.entity1_type_id,
          entity1_id_field_name: relationship.entity1_id_field_name,
          entity2_type_id: relationship.entity2_type_id,
          entity2_id_field_name: relationship.entity2_id_field_name,
        };
  if (importType === ImportType.object && objectTypeId != null) {
    body.object_type_id = Number(objectTypeId);
  }
  return body;
}

/**
 * What a successful preview vouched for: every field the backend read, minus the import id (which
 * a first save assigns without changing what was previewed). A step stays unlocked only while the
 * body it would send matches the one that previewed.
 */
export function previewFingerprint(body: SqlImportPreviewInput): string {
  const { import_id: _importId, ...rest } = body;
  return JSON.stringify(Object.entries(rest).sort(([a], [b]) => a.localeCompare(b)));
}

/** Whether a preview counts as a successful query: it ran and raised no blocking validation. */
export function isPreviewSuccessful(result: SqlImportPreviewResult): boolean {
  return isEmptyString(result.error) && result.validations.errors.length === 0;
}

export type PreviewErrorCopy = { key: string; title: string; message: string };

/**
 * A blocking validation as a title and message. `query_not_parseable` carries the parser's own
 * text after a colon; the others are fixed. Unknown codes are shown as they come.
 */
export function previewErrorCopy(error: string, people: string): PreviewErrorCopy {
  if (error.startsWith('query_not_parseable')) {
    const detail = error.replace(/^query_not_parseable:?\s*/, '');
    return {
      key: 'query_not_parseable',
      title: 'Could not parse your query.',
      message: isEmptyString(detail)
        ? 'Make sure your query is valid SQL and try again.'
        : `Your query returned an error: ${detail}. Make sure your query is valid SQL and try again.`,
    };
  }
  switch (error) {
    case 'id_not_present':
      return {
        key: error,
        title: "We didn't find an id or email selected in your column names.",
        message: `Select or map a column to at least one identifier for ${people} that you import to this workspace.`,
      };
    case 'email_not_present':
      return {
        key: error,
        title: "We didn't find an email in your column names.",
        message: `An email is required to import ${people} in this workspace.`,
      };
    case 'forbidden_query':
      return {
        key: error,
        title: 'We cannot run your query.',
        message:
          'Queries must be read-only. Your query attempts write changes to your database. Update your query to make it read-only.',
      };
    default:
      return { key: error, title: 'Your query returned an error.', message: error };
  }
}

export type PreviewWarningCopy = { title: string; message: string; learnMoreUrl?: string };

const SENSITIVE_FIELDS_PREFIX = 'sensitive_fields_found: ';

/** A non-blocking validation as a title and message. Unknown codes are shown as they come. */
export function previewWarningCopy(warning: string): PreviewWarningCopy {
  if (warning.startsWith(SENSITIVE_FIELDS_PREFIX)) {
    const fields = warning.slice(SENSITIVE_FIELDS_PREFIX.length);
    return {
      title: `You've included at least one field (${fields}) that you should not import into Customer.io.`,
      message:
        'These fields include passwords, credit card numbers, or personal identifiable information (PII) for people under 18 years old.',
    };
  }
  switch (warning) {
    case 'select_star_query':
      return {
        title: '* queries are not recommended.',
        message: 'Specify the columns you want to sync to your workspace.',
      };
    case 'limit_not_present':
      return {
        title: 'You are not using {{last_sync_time}} to limit your sync.',
        message:
          'We strongly recommend that you limit your query to the columns that changed since the last sync using {{last_sync_time}}.',
        learnMoreUrl: 'https://docs.customer.io/sql-sync/#last-sync-time',
      };
    case 'result_not_present':
      return {
        title: 'There are no results present.',
        message: 'The query is syntactically correct but returned no results.',
      };
    case 'not_a_select_query':
      return {
        title: 'Non-select queries are not recommended.',
        message: 'The query should only be selecting the data you want to sync.',
      };
    default:
      return { title: 'Your query raised a warning.', message: warning };
  }
}
